import { COOKIE_KEYS } from "../constants";
import { deleteCookie } from "./cookieUtils";
import { clearOrganizationProceduresCache } from "./organizationProceduresCache";

// BroadcastChannel for cross-tab communication
let authChannel: BroadcastChannel | null = null;

// Initialize BroadcastChannel if supported
if (typeof BroadcastChannel !== "undefined") {
  authChannel = new BroadcastChannel("auth_channel");
}

// Unique ID for this tab
const TAB_ID = `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

// Leader election constants
const LEADER_KEY = "token_refresh_leader";
const LEADER_TIMEOUT = 10000; // 10 seconds - leader considered dead if no heartbeat

export type AuthSyncMessage = {
  type: "LOGOUT" | "LOGIN" | "TOKEN_REFRESHED" | "LEADER_HEARTBEAT";
  timestamp: number;
  tabId?: string;
};

interface LeaderInfo {
  tabId: string;
  timestamp: number;
}

/**
 * Try to become the leader for token refresh
 * Returns true if this tab is now the leader
 */
export const tryBecomeLeader = (): boolean => {
  try {
    const leaderData = localStorage.getItem(LEADER_KEY);
    const now = Date.now();

    if (leaderData) {
      const leader: LeaderInfo = JSON.parse(leaderData);

      // Check if current leader is still alive (heartbeat within timeout)
      if (leader.tabId !== TAB_ID && now - leader.timestamp < LEADER_TIMEOUT) {
        // Another tab is the active leader
        return false;
      }
    }

    // No leader or leader timed out - become the leader
    const newLeader: LeaderInfo = { tabId: TAB_ID, timestamp: now };
    localStorage.setItem(LEADER_KEY, JSON.stringify(newLeader));

    // Verify we actually got the lock (handle race condition)
    const verification = localStorage.getItem(LEADER_KEY);
    if (verification) {
      const verified: LeaderInfo = JSON.parse(verification);
      return verified.tabId === TAB_ID;
    }

    return false;
  } catch (e) {
    console.error("[authSync] Error in tryBecomeLeader:", e);
    return false;
  }
};

/**
 * Check if this tab is currently the leader
 */
export const isLeader = (): boolean => {
  try {
    const leaderData = localStorage.getItem(LEADER_KEY);
    if (!leaderData) return false;

    const leader: LeaderInfo = JSON.parse(leaderData);
    return leader.tabId === TAB_ID;
  } catch (e) {
    return false;
  }
};

/**
 * Update leader heartbeat timestamp
 */
export const updateLeaderHeartbeat = (): void => {
  if (!isLeader()) return;

  try {
    const newLeader: LeaderInfo = { tabId: TAB_ID, timestamp: Date.now() };
    localStorage.setItem(LEADER_KEY, JSON.stringify(newLeader));
  } catch (e) {
    console.error("[authSync] Error updating heartbeat:", e);
  }
};

/**
 * Release leadership (e.g., when tab closes)
 */
export const releaseLeadership = (): void => {
  if (!isLeader()) return;

  try {
    localStorage.removeItem(LEADER_KEY);
  } catch (e) {
    console.error("[authSync] Error releasing leadership:", e);
  }
};

/**
 * Get current tab ID
 */
export const getTabId = (): string => TAB_ID;

/**
 * Broadcast logout event to all tabs
 */
export const broadcastLogout = () => {
  if (authChannel) {
    const message: AuthSyncMessage = {
      type: "LOGOUT",
      timestamp: Date.now(),
    };
    authChannel.postMessage(message);
  }
};

/**
 * Broadcast login event to all tabs
 */
export const broadcastLogin = () => {
  if (authChannel) {
    const message: AuthSyncMessage = {
      type: "LOGIN",
      timestamp: Date.now(),
    };
    authChannel.postMessage(message);
  }
};

/**
 * Broadcast token refresh event to all tabs
 */
export const broadcastTokenRefresh = () => {
  if (authChannel) {
    const message: AuthSyncMessage = {
      type: "TOKEN_REFRESHED",
      timestamp: Date.now(),
    };
    authChannel.postMessage(message);
  }
};

/**
 * Listen for auth events from other tabs
 */
export const listenToAuthEvents = (callbacks: {
  onLogout: () => void;
  onLogin?: () => void;
  onTokenRefreshed?: () => void;
}) => {
  if (!authChannel) {
    console.warn("BroadcastChannel not supported");
    return () => {};
  }

  const handleMessage = (event: MessageEvent<AuthSyncMessage>) => {
    const { type } = event.data;

    if (type === "LOGOUT") {
      console.log("Logout event received from another tab");
      callbacks.onLogout();
    } else if (type === "LOGIN" && callbacks.onLogin) {
      console.log("Login event received from another tab");
      callbacks.onLogin();
    } else if (type === "TOKEN_REFRESHED" && callbacks.onTokenRefreshed) {
      console.log("Token refresh event received from another tab");
      callbacks.onTokenRefreshed();
    }
  };

  authChannel.addEventListener("message", handleMessage);

  // Return cleanup function
  return () => {
    authChannel?.removeEventListener("message", handleMessage);
  };
};

/**
 * Clear all auth data from localStorage and cookies.
 * Call this on logout or when tokens are invalidated.
 */
export const clearAuthData = () => {
  deleteCookie(COOKIE_KEYS.ACCESS_TOKEN);
  deleteCookie(COOKIE_KEYS.REFRESH_TOKEN);
  clearOrganizationProceduresCache();
};
